import React, { useEffect } from 'react';
import { Redirect, Switch, useLocation } from 'react-router-dom';
import Route from './Route';
import { isProductionHost } from 'lib/env';
import { VERTICAL } from 'lib/utils/store/vehicle';
import { USL } from 'lib/alma/alma';
import { changeLanguage } from 'i18next';

import PrivateRoute from './PrivateRoute';
import RouteDebugger from './Util/RouteDebugger';
import RedirectAd, { REDIRECT_AD_ACTIONS } from './Routes/Redirect/RedirectAd';
import LoadingIndicator from './LoadingIndicator';
import { Me } from 'types/types';
import moment from 'moment';
import cookies from 'lib/cookies';
import { logException } from 'lib/errors';

const Home = React.lazy(() => import('scenes/Home'));
const Faq = React.lazy(() => import('scenes/Faq'));
const ContactUs = React.lazy(() => import('scenes/ContactUs'));
const BosTosLegacyV3 = React.lazy(() => import('./BillOfSale/BosTosLegacy/v3/BosTosLegacy'));
const BosTosLegacyV2 = React.lazy(() => import('./BillOfSale/BosTosLegacy/v2/BosTosLegacy'));
const BosTosLegacyV1 = React.lazy(() => import('./BillOfSale/BosTosLegacy/v1/BosTosLegacy'));
const BosTosView = React.lazy(() => import('./BillOfSale/BosTos/BosTosView'));
const MyNettixInfo = React.lazy(() => import('scenes/MyNettixInfo'));
const UserEdit = React.lazy(() => import('./User/UserEdit/UserEdit'));
const AddVehicleView = React.lazy(() => import('./Vehicles/AddVehicle/AddVehicleView/AddVehicleView'));
const VehicleProfileView = React.lazy(() => import('./Vehicles/VehicleProfile/VehicleProfileView'));
const BillOfSaleView = React.lazy(() => import('./BillOfSale/BosView'));
const EditBillOfSale = React.lazy(() => import('./BillOfSale/EditBillOfSale/EditBillOfSale'));
const PaymentSuccess = React.lazy(() => import('./Payment/PaymentSuccess'));
const DisputePayment = React.lazy(() => import('./Payment/DisputePayment'));
const DisputeView = React.lazy(() => import('./Payment/DisputeView'));
const PageNotFound = React.lazy(() => import('./PageNotFound'));
const ServiceProcessDiagram = React.lazy(() => import('./ServiceProcessDiagram/ServiceProcessDiagram'));
const AdminDashboard = React.lazy(() => import('./Admin/AdminDashboard'));
const PaymentSettlementView = React.lazy(() => import('./Admin/Payment/PaymentSettlementView'));
const BosCreateView = React.lazy(() => import('./BillOfSale/BosCreate/BosCreateView'));
const AddVehicleFromAd = React.lazy(() => import('./Vehicles/AddVehicleFromAd/AddVehicleFromAd'));
const MakeOfferView = React.lazy(() => import('./Offer/MakeOffer/MakeOfferView'));
const CreateAdView = React.lazy(() => import('./Vehicles/CreateAd/CreateAdView'));
const BosListRedirect = React.lazy(() => import('./BillOfSale/BosList/BosListRedirect'));
const BillOfSaleLander = React.lazy(() => import('./BillOfSale/BillOfSaleLander/BillOfSaleLander'));
const BillOfSalesLanderView = React.lazy(() => import('./BillOfSale/BillOfSalesLander/BillOfSalesLanderView'));
const LandingPriceEstimateView = React.lazy(() => import('./Landing/LandingPriceEstimateView/LandingPriceEstimateView'));
const LandingTimelineView = React.lazy(() => import('./Landing/LandingTimelineView/LandingTimelineView'));
const UserActivationView = React.lazy(() => import('./User/UserActivationView/UserActivationView'));
const VehicleProfileShareView = React.lazy(() => import('./Vehicles/VehicleProfile/VehicleProfileShare/VehicleProfileShareView'));
const UserDocumentsView = React.lazy(() => import('./UserDocuments/UserDocumentsView'));
const MakeOfferWithRegNumView = React.lazy(() => import('./Offer/MakeOffer/MakeOfferWithRegNumView'));
const OfferViewLoader = React.lazy(() => import('./Offer/OfferView/OfferViewLoader'));
const DevErrorView = React.lazy(() => import('./Errors/DevErrorView'));
const FinancingApplicationView = React.lazy(() => import('./Financing/FinancingApplication/FinancingApplicationView/FinancingApplicationView'));
const FinancingApplicationFormView = React.lazy(() => import('./Financing/FinancingApplication/FinancingApplicationFormView'));
const FinancingApplicationConfirmation = React.lazy(() => import('./Financing/FinancingApplication/FinancingApplicationConfirmation/FinancingApplicationConfirmation'));
const FinancingApplicationSuccess = React.lazy(() => import('./Financing/FinancingApplication/FinancingApplicationSuccess/FinancingApplicationSuccess'));
// const FinancingApplicationStartView = React.lazy(() => import('./Financing/FinancingApplication/FinancingApplicationStartView'));
const UserIdentificationTestDataInputView = React.lazy(() => import('./User/UserIdentification/UserIdentificationTestDataInputView/UserIdentificationTestDataInputView'));
const AuthVerifyEmailView = React.lazy(() => import('./Auth/AuthVerifyEmailView/AuthVerifyEmailView'));
// const LandingFinanceViewNew = React.lazy(() => import('./Landing/LandingFinanceView/LandingFinanceViewNew'));
const ReferralHandler = React.lazy(() => import('./Navigation/ReferralHandler'));
const AuthLogin = React.lazy(() => import('./Auth/AuthLogin'));
const UserIdentificationWithAnotherAccountError = React.lazy(() => import('./User/UserIdentification/UserIdentificationWithAnotherAccountError/UserIdentificationWithAnotherAccountError'));
const PGWLander = React.lazy(() => import('components/BillOfSale/BosIdentification/PGWLander'));
const VerifyLander = React.lazy(() => import('components/BillOfSale/BosIdentification/VerifyLander'));
const PGWMockLander = React.lazy(() => import('components/BillOfSale/BosIdentification/PGWMockLander'));
const OwnerLander = React.lazy(() => import('components/BillOfSale/BosIdentification/OwnerLander'));

export const ViewRouterContext = React.createContext({});

/**
 * OmaNettix specific primary categories.
 * @type {Object}
 */
const PRIMARY_CATEGORY = Object.freeze({
    dashboard: 'dashboard', // dashboard, frontpage...
    omanettix: 'omanettix', // info, about us...
    bos: 'bos', // add bos, bos process, bos landing, bos preview etc...
    vehicle: 'vehicle', // add vehicle, vehicle timeline, vehicle details...
    financing: 'financing', // financing application form, application page...
    buyeroffer: 'buyeroffer', // offer landing, offer form, offer page...
    user: 'user', // user auth, user profile...
    admin: 'admin', // all admin only pages...
    dev: 'dev', // all pages that are only accessible in dev env...
    error: 'error', // error pages, such as 404...
});

/**
 * Page types are from Alma dataLayer specification.
 * https://github.com/almamedia/datalayer
 * @type {Object}
 */
const PAGE_TYPE = Object.freeze({
    frontpage: 'frontpage',
    sectionfrontpage: 'sectionfrontpage',
    article: 'article',
    blogpost: 'blogpost',
    searchpage: 'searchpage',
    searchresultpage: 'searchresultpage',
    itempage: 'itempage',
    otherpage: 'otherpage',
    processpage: 'processpage',
    shoppingcart: 'shoppingcart',
    errorpage: 'errorpage',
});

type RouteRenderFunction = (props: {match, location, history}) => JSX.Element;

interface IViewRouterProps {
    loggedIn: boolean;
    currentUser: {
        me: Me
    };
    currentUserLoading: boolean;
}

interface IRouteUserSourceLabeling {
    source: string;
    type: string;
}

interface IViewRoute {
    id?: string;
    private: boolean;
    pageType: string;
    primaryCategory: string;
    exact: boolean;
    path: string;
    component?: React.ReactNode;
    render?: RouteRenderFunction;
    loginUserSourceLabeling?: IRouteUserSourceLabeling;
}

interface IRedirect {
    exact: boolean;
    from: string;
    to: string;
}

class ViewRoute {
    private readonly data: IViewRoute;

    constructor (data: IViewRoute) {
        if (!data.render && !data.component) {
            throw new Error(`Invalid route "${data.path}". "render" or "component" prop is required!`);
        } else if (data.render && data.component) {
            throw new Error('Invalid route. "render" and "component" should not be defined at the same time.');
        }

        this.data = data;
    }

    getData (): IViewRoute {
        return this.data;
    }
}

class ViewRedirect {
    private readonly data: IRedirect;

    constructor (data: IRedirect) {
        this.data = data;
    }

    getData (): IRedirect {
        return this.data;
    }
}

const USER_ROUTES: ReadonlyArray<ViewRoute | ViewRedirect> = [
    new ViewRoute({
        path: '/:lang(en)?/',
        exact: true,
        private: false,
        pageType: PAGE_TYPE.frontpage,
        primaryCategory: PRIMARY_CATEGORY.dashboard,
        component: Home,
    }),
    new ViewRoute({
        path: '/:lang(en)?/auton-hinta-arvio',
        exact: true,
        private: false,
        render: (props) => <LandingPriceEstimateView {...props} vertical="car" />,
        pageType: PAGE_TYPE.sectionfrontpage,
        primaryCategory: PRIMARY_CATEGORY.vehicle,
    }),
    new ViewRoute({
        path: '/:lang(en)?/moton-hinta-arvio',
        exact: true,
        private: false,
        render: (props) => <LandingPriceEstimateView {...props} vertical="bike" />,
        pageType: PAGE_TYPE.sectionfrontpage,
        primaryCategory: PRIMARY_CATEGORY.vehicle,
    }),
    new ViewRoute({
        path: '/:lang(en)?/aikajana',
        exact: true,
        private: false,
        component: LandingTimelineView,
        pageType: PAGE_TYPE.sectionfrontpage,
        primaryCategory: PRIMARY_CATEGORY.vehicle,
    }),
    new ViewRoute({
        path: '/:lang(en)?/autorahoitus',
        exact: true,
        private: false,
        component: ReferralHandler,
        pageType: PAGE_TYPE.sectionfrontpage,
        primaryCategory: PRIMARY_CATEGORY.financing,
    }),
    new ViewRoute({
        path: '/:lang(en)?/fiksut-kaupat',
        exact: true,
        private: false,
        component: BillOfSaleLander,
        pageType: PAGE_TYPE.sectionfrontpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRoute({
        path: '/:lang(en)?/landing/bill-of-sale/:variant(financed)',
        exact: true,
        private: false,
        component: BillOfSaleLander,
        pageType: PAGE_TYPE.sectionfrontpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRoute({
        path: '/:lang(en)?/fiksut-kaupat/:vertical(car|bike|caravan|part|boat|machine)/:adId',
        exact: true,
        private: false,
        component: BillOfSaleLander,
        pageType: PAGE_TYPE.sectionfrontpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRedirect({
        exact: true,
        from: '/:lang(en)?/palveluprosessi',
        to: '/:lang(en)?/fiksut-kaupat',
    }),
    new ViewRoute({
        path: '/:lang(en)?/ota-yhteytta',
        exact: false,
        private: false,
        component: ContactUs,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.omanettix,
    }),
    new ViewRoute({
        path: '/:lang(en)?/sahkoisen-kaupan-palveluehdot/1',
        exact: false,
        private: false,
        component: BosTosLegacyV1,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRoute({
        path: '/:lang(en)?/sahkoisen-kaupan-palveluehdot/2',
        exact: false,
        private: false,
        component: BosTosLegacyV2,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRoute({
        path: '/:lang(en)?/sahkoisen-kaupan-palveluehdot/3',
        exact: false,
        private: false,
        component: BosTosLegacyV3,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRoute({
        path: '/:lang(en)?/sahkoisen-kaupan-palveluehdot/:version',
        exact: false,
        private: false,
        component: BosTosView,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRoute({
        path: '/:lang(en)?/sahkoisen-kaupan-palveluehdot',
        exact: false,
        private: false,
        component: BosTosView,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRoute({
        path: '/:lang(en)?/my-nettix-info',
        exact: false,
        private: false,
        component: MyNettixInfo,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.omanettix,
    }),
    new ViewRoute({
        path: '/:lang(en)?/kauppakirjat',
        private: false,
        exact: true,
        component: BillOfSalesLanderView,
        pageType: PAGE_TYPE.sectionfrontpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRedirect({
        from: '/:lang(en)?/ad/:vertical/:adId',
        to: '/:lang(en)?/uudelleenohjaus/:vertical/:adId',
        exact: true,
    }),
    new ViewRoute({
        path: '/:lang(en)?/uudelleenohjaus/:vertical/:adId',
        private: false,
        exact: true,
        component: RedirectAd,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.vehicle,
    }),
    new ViewRoute({
        path: `/:lang(en)?/uudelleenohjaus/:vertical/:adId/:action(${Object.values(REDIRECT_AD_ACTIONS).join('|')})`,
        exact: true,
        private: false,
        component: RedirectAd,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.vehicle,
    }),
    new ViewRedirect({
        from: '/:lang(en)?/bill-of-sale/landing',
        to: '/:lang(en)?/fiksut-kaupat',
        exact: true,
    }),
    new ViewRedirect({
        from: '/:lang(en)?/bill-of-sale/landing/:vertical/:adId',
        to: '/:lang(en)?/fiksut-kaupat/:vertical/:adId',
        exact: true,
    }),
    new ViewRoute({
        path: '/:lang(en)?/fiksut-kaupat/kaupan-vaiheet',
        exact: true,
        private: false,
        component: ServiceProcessDiagram,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRoute({
        path: '/:lang(en)?/bill-of-sale/process/:variant(financed)',
        exact: true,
        private: false,
        component: ServiceProcessDiagram,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRoute({
        path: '/:lang(en)?/fiksut-kaupat/uudelleenohjaus',
        exact: true,
        private: false,
        component: BosListRedirect,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRoute({
        path: '/:lang(en)?/fiksut-kaupat/sahkoinen-kauppakirja/:bosId/pyynto/:requestId/vaihe/:task/:status',
        exact: true,
        private: false,
        component: BillOfSaleView,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRoute({
        path: '/:lang(en)?/fiksut-kaupat/sahkoinen-kauppakirja/:bosId/maksu/onnistunut',
        exact: true,
        private: false,
        component: PaymentSuccess,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRoute({
        path: '/:lang(en)?/fiksut-kaupat/sahkoinen-kauppakirja/:bosId(\\d+)',
        exact: true,
        private: false,
        component: BillOfSaleView,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRoute({
        path: '/:lang(en)?/fiksut-kaupat/sahkoinen-kauppakirja/:bosId/katsele',
        exact: true,
        private: false,
        component: BillOfSaleView,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRedirect({
        from: '/:lang(en)?/offers/:offerId',
        to: '/:lang(en)?/tarjous/:offerId',
        exact: true,
    }),
    new ViewRedirect({
        from: '/:lang(en)?/offers',
        to: '/:lang(en)?/kayttaja/asiakirjat/tarjoukset',
        exact: true,
    }),
    new ViewRedirect({
        from: '/:lang(en)?/user/documents',
        to: '/:lang(en)?/kayttaja/asiakirjat/tarjoukset',
        exact: true,
    }),
    new ViewRedirect({
        from: '/:lang(en)?/bill-of-sale',
        to: '/:lang(en)?/kayttaja/asiakirjat/kauppakirjat',
        exact: true,
    }),
    new ViewRoute({
        path: '/:lang(en)?/kayttaja/asiakirjat/tarjoukset',
        exact: true,
        private: true,
        component: UserDocumentsView,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.buyeroffer,
    }),
    new ViewRoute({
        path: '/:lang(en)?/kayttaja/asiakirjat/kauppakirjat',
        exact: true,
        private: true,
        component: UserDocumentsView,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRedirect({
        exact: true,
        from: '/:lang(en)?/user/documents/financing',
        to: '/:lang(en)?/',
    }),
    /* new ViewRoute({
        path: '/:lang(en)?/user/documents/financing',
        exact: true,
        private: true,
        component: UserDocumentsView,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.financing,
    }), */
    new ViewRoute({
        path: '/:lang(en)?/tarjous/:offerId(\\d+)',
        exact: true,
        private: false,
        component: OfferViewLoader,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.buyeroffer,
    }),
    new ViewRoute({
        path: '/:lang(en)?/tarjous/rekisteritunnus/:regNumber',
        exact: true,
        private: true,
        component: MakeOfferWithRegNumView,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.buyeroffer,
    }),
    new ViewRedirect({
        from: '/:lang(en)?/tarjous/:vertical/:adId',
        to: '/:lang(en)?/tarjous/:vertical/ilmoitus/:adId',
        exact: true,
    }),
    new ViewRoute({
        path: '/:lang(en)?/tarjous/:vertical/ilmoitus/:adId',
        exact: true,
        private: true,
        component: MakeOfferView,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.buyeroffer,
        loginUserSourceLabeling: { source: USL.SOURCE.digital_bill_of_sale_start_purchase, type: USL.TYPE.permanent },
    }),
    new ViewRoute({
        path: '/:lang(en)?/fiksut-kaupat/sahkoinen-kauppakirja/:bosId/jaa/:bosShareLinkHash',
        exact: true,
        private: false,
        component: BillOfSaleView,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRoute({
        path: '/:lang(en)?/fiksut-kaupat/sahkoinen-kauppakirja/:bosId/muokkaa',
        exact: true,
        private: true,
        component: EditBillOfSale,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRoute({
        path: '/:lang(en)?/fiksut-kaupat/sahkoinen-kauppakirja/:bosId/katsele',
        exact: true,
        private: true,
        component: BillOfSaleView,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRoute({
        path: '/:lang(en)?/fiksut-kaupat/sahkoinen-kauppakirja/:bosId/esikatselu',
        exact: true,
        private: true,
        component: BillOfSaleView,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRoute({
        path: '/:lang(en)?/fiksut-kaupat/sahkoinen-kauppakirja/:bosId/selvityspyynto',
        exact: true,
        private: true,
        component: DisputePayment,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRoute({
        path: '/:lang(en)?/fiksut-kaupat/sahkoinen-kauppakirja/:bosId/selvityspyynto/:disputeId',
        exact: true,
        private: true,
        component: DisputeView,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRedirect({
        from: '/:lang(en)?/user/profile',
        to: '/:lang(en)?/kayttaja/muokkaa',
        exact: true,
    }),
    new ViewRoute({
        path: '/login',
        exact: true,
        private: false,
        id: 'login',
        component: AuthLogin,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.user,
    }),
    new ViewRoute({
        path: '/:lang(en)?/user/activation/:status(success|failed)',
        exact: true,
        private: false,
        id: 'user-activation',
        component: UserActivationView,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.user,
    }),
    new ViewRoute({
        path: '/:lang(en)?/user/register/verify',
        id: 'user-verify-email',
        exact: true,
        private: false,
        component: AuthVerifyEmailView,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.user,
    }),
    new ViewRoute({
        path: '/:lang(en)?/kayttaja/muokkaa',
        exact: false,
        private: true,
        component: UserEdit,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.user,
    }),
    new ViewRoute({
        path: '/:lang(en)?/lisaa-ajoneuvo',
        id: 'add-vehicle',
        exact: true,
        private: false,
        component: AddVehicleView,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.vehicle,
    }),
    new ViewRoute({
        path: '/:lang(en)?/lisaa-ajoneuvo/ilmoitus/:vertical/:adId',
        exact: true,
        private: true,
        component: AddVehicleFromAd,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.vehicle,
    }),
    new ViewRoute({
        path: '/:lang(en)?/lisaa-ajoneuvo/ilmoitus/:vertical/:adId/:action',
        exact: true,
        private: true,
        component: AddVehicleFromAd,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.vehicle,
    }),
    new ViewRoute({
        path: '/:lang(en)?/luo-sahkoinen-kauppakirja',
        exact: true,
        private: true,
        component: BosCreateView,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
        loginUserSourceLabeling: { source: USL.SOURCE.digital_bill_of_sale_start_digital_sale, type: USL.TYPE.permanent },
    }),
    new ViewRedirect({
        from: '/:lang(en)?/ajoneuvo/:vehicleId',
        to: '/:lang(en)?/ajoneuvo/:vehicleId/tapahtumat',
        exact: true,
    }),
    new ViewRoute({
        path: '/:lang(en)?/ajoneuvo/:vehicleId/jaa/:shareHash',
        exact: true,
        private: false,
        component: VehicleProfileShareView,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.vehicle,
    }),
    new ViewRoute({
        path: '/:lang(en)?/ajoneuvo/:vehicleId/:tab(tiedot|hallinnointi|tapahtumat)',
        exact: true,
        private: true,
        component: VehicleProfileView,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.vehicle,
    }),
    new ViewRoute({
        path: '/:lang(en)?/ajoneuvo/:vehicleId/ilmoitus/luo',
        exact: true,
        private: true,
        component: CreateAdView,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.vehicle,
    }),
    new ViewRoute({
        path: '/:lang(en)?/ajoneuvo/:vehicleId/luo-sahkoinen-kauppakirja',
        exact: true,
        private: true,
        component: BosCreateView,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.vehicle,
    }),
    new ViewRedirect({
        exact: false,
        from: `/:lang(en)?/financing/application/new/ad/:vertical(${VERTICAL.CAR})/:adId`,
        to: '/:lang(en)?/',
    }),
    /* new ViewRoute({
        path: `/financing/application/new/ad/:vertical(${VERTICAL.CAR})/:adId`,
        exact: true,
        private: false,
        component: FinancingApplicationStartView,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.financing,
        loginUserSourceLabeling: { source: USL.SOURCE.financing_login, type: USL.TYPE.permanent },
    }), */
    new ViewRoute({
        path: '/:lang(en)?/financing/application/:financingApplicationId/edit',
        exact: true,
        private: true,
        component: FinancingApplicationFormView,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.financing,
    }),
    new ViewRoute({
        path: '/:lang(en)?/financing/application/:financingApplicationId',
        exact: true,
        private: true,
        component: FinancingApplicationView,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.financing,
    }),
    new ViewRoute({
        path: '/:lang(en)?/financing/application/:financingApplicationId/confirmation',
        exact: true,
        private: false,
        component: FinancingApplicationConfirmation,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.financing,
    }),
    new ViewRoute({
        path: '/:lang(en)?/financing/application/:financingApplicationId/success',
        exact: true,
        private: false,
        component: FinancingApplicationSuccess,
        pageType: PAGE_TYPE.processpage,
        primaryCategory: PRIMARY_CATEGORY.financing,
    }),
    new ViewRoute({
        path: '/:lang(en)?/virhe-tunnistautumisessa/:errorCode/:vertical?/:vehicleId?',
        exact: true,
        private: true,
        component: UserIdentificationWithAnotherAccountError,
        pageType: PAGE_TYPE.errorpage,
        primaryCategory: PRIMARY_CATEGORY.error,
    }),
    new ViewRoute({
        path: '/:lang(en)?/jata-ilmoitus/:vehicleId/:action(aloita-kaupanteko)?',
        exact: true,
        private: true,
        component: PGWLander,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRoute({
        path: '/:lang(en)?/ilmoituksen-varmentaminen/:vehicleId/:action(aloita-kaupanteko)?',
        exact: true,
        private: true,
        component: VerifyLander,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
    new ViewRoute({
        path: '/:lang(en)?/jata-ilmoitus/rajoitetiedot/:vehicleId/:action(aloita-kaupanteko)?',
        exact: true,
        private: true,
        component: OwnerLander,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
    }),
];

const ADMIN_ROUTES: ReadonlyArray<ViewRoute | ViewRedirect> = [
    new ViewRoute({
        path: '/:lang(en)?/admin',
        exact: true,
        private: true,
        component: AdminDashboard,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.admin,
    }),
    new ViewRoute({
        path: '/:lang(en)?/admin/kauppakirja/:bosId/maksu',
        exact: true,
        private: true,
        component: PaymentSettlementView,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.admin,
    }),
];

const DEV_ROUTES: ReadonlyArray<ViewRoute | ViewRedirect> = !isProductionHost() ? [
    new ViewRoute({
        path: '/:lang(en)?/faq',
        private: false,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.omanettix,
        exact: true,
        component: Faq,
    }),
    new ViewRoute({
        path: '/:lang(en)?/dev/error/:type/:code',
        exact: true,
        private: false,
        component: DevErrorView,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.dev,
    }),
    new ViewRoute({
        path: '/:lang(en)?/dev/user-identification-test-data-input',
        exact: true,
        private: true,
        component: UserIdentificationTestDataInputView,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.dev,
    }),
    // TODO::ACCU routing to remove - MikaelP
    new ViewRoute({
        path: '/:lang(en)?/accu',
        private: false,
        pageType: PAGE_TYPE.otherpage,
        primaryCategory: PRIMARY_CATEGORY.bos,
        exact: true,
        component: PGWMockLander,
    }),
] : [];

const ROUTES: ReadonlyArray<ViewRoute | ViewRedirect> = [
    ...USER_ROUTES,
    ...ADMIN_ROUTES,
    ...DEV_ROUTES,
    // 404 not found route important to print last
    new ViewRoute({
        path: '/:lang(en)?/*',
        exact: false,
        private: false,
        component: PageNotFound,
        pageType: PAGE_TYPE.errorpage,
        primaryCategory: PRIMARY_CATEGORY.error,
    })
];

const ViewRouter = ({ loggedIn, currentUser, currentUserLoading }: IViewRouterProps) => {
    const location = useLocation();

    // Extract language from the URL
    const urlLang = location.pathname.includes('/en') ? 'en' : 'fi';

    useEffect(() => {
        // Set language using i18n
        changeLanguage(urlLang).catch(error => {
            logException(error);
        });
        cookies.set('lang', urlLang);
        moment.locale(urlLang);
    }, [urlLang]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <RouteDebugger enabled={true}>
            <ViewRouterContext.Provider value={{ loggedIn, currentUser, currentUserLoading }}>
                <React.Suspense fallback={<LoadingIndicator />}>
                    <Switch>
                        {ROUTES.map((r) => {
                            if (r instanceof ViewRoute) {
                                const data: IViewRoute = r.getData();
                                const RouteComponent: React.ElementType = data.private ? PrivateRoute : Route;

                                return (
                                    <RouteComponent
                                        key={data.path}
                                        path={data.path}
                                        id={data.id}
                                        primaryCategory={data.primaryCategory}
                                        pageType={data.pageType}
                                        exact={data.exact}
                                        component={data.component}
                                        render={data.render}
                                    />
                                );
                            } else if (r instanceof ViewRedirect) {
                                const data: IRedirect = r.getData();

                                return (
                                    <Redirect
                                        key={data.from + '&' + data.to}
                                        exact={data.exact}
                                        from={data.from}
                                        to={data.to}
                                    />
                                );
                            } else {
                                throw new Error('Invalid route instance.');
                            }
                        })}
                    </Switch>
                </React.Suspense>
            </ViewRouterContext.Provider>
        </RouteDebugger>
    );
};

export default ViewRouter;
